import { useContext } from 'react';
import Scrollbar from 'src/components/Scrollbar';
import { SidebarContext } from 'src/contexts/SidebarContext';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Drawer,
  alpha,
  styled,
  Divider,
  useTheme,
  lighten,
  darken,
  Tooltip,
  IconButton
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import SidebarMenu from './SidebarMenu';
import Logo from 'src/components/LogoSign';

const SidebarWrapper = styled(Box)(
  ({ theme }) => `
        width: ${theme.sidebar.width};
        min-width: ${theme.sidebar.width};
        color: ${theme.colors.alpha.trueWhite[70]};
        z-index: 7;
        height: 100%;
`
);

function Sidebar() {
  const { sidebarToggle, toggleSidebar, desktopSidebarOpen, toggleDesktopSidebar } =
    useContext(SidebarContext);
  const closeSidebar = () => toggleSidebar();
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  // Check if current language is Arabic/RTL
  const isRTL = i18n.language === 'ar' || theme.direction === 'rtl';

  return (
    <>
      <SidebarWrapper
        sx={{
          display: {
            xs: 'none',
            lg: 'inline-block'
          },
          position: 'fixed',
          top: 80,
          height: 'calc(100% - 80px)',
          // Position sidebar on right for RTL, left for LTR
          ...(isRTL ? { right: 0 } : { left: 0 }),
          // Slide the sidebar off-screen when closed on desktop
          transform: desktopSidebarOpen
            ? 'translateX(0)'
            : `translateX(${isRTL ? '100%' : '-100%'})`,
          visibility: desktopSidebarOpen ? 'visible' : 'hidden',
          transition: theme.transitions.create(['transform', 'visibility'], {
            duration: theme.transitions.duration.standard
          }),
          background:
            theme.palette.mode === 'dark'
              ? alpha(lighten(theme.header.background, 0.1), 0.25)
              : darken(theme.colors.alpha.black[100], 0.5),
          boxShadow:
            theme.palette.mode === 'dark' ? theme.sidebar.boxShadow : 'none'
        }}
      >
        <Scrollbar>
          <Divider
            sx={{
              mt: theme.spacing(3),
              mx: theme.spacing(2),
              background: theme.colors.alpha.trueWhite[10]
            }}
          />
          <SidebarMenu />
        </Scrollbar>
      </SidebarWrapper>
      <Box
        sx={{
          display: {
            xs: 'none',
            lg: 'flex'
          },
          position: 'fixed',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 8,
          transition: theme.transitions.create(['left', 'right'], {
            duration: theme.transitions.duration.standard
          }),
          // Docked on the sidebar edge when open, flush to the screen edge when closed
          ...(isRTL
            ? {
                right: desktopSidebarOpen
                  ? `calc(${theme.sidebar.width} - 18px)`
                  : 8
              }
            : {
                left: desktopSidebarOpen
                  ? `calc(${theme.sidebar.width} - 18px)`
                  : 8
              })
        }}
      >
        <Tooltip arrow title={t('sidebar.toggle_menu')}>
          <IconButton
            size="small"
            onClick={toggleDesktopSidebar}
            sx={{
              background: '#18E68E',
              border: '1px solid #18E68E',
              boxShadow: theme.sidebar.boxShadow,
              color: '#000',
              '&:hover': {
                background: '#18E68E'
              }
            }}
          >
            {desktopSidebarOpen === !isRTL ? (
              <ChevronLeftIcon fontSize="small" sx={{ color: '#000' }} />
            ) : (
              <ChevronRightIcon fontSize="small" sx={{ color: '#000' }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      <Drawer
        sx={{
          boxShadow: `${theme.sidebar.boxShadow}`
        }}
        // Drawer opens from right in RTL, left in LTR
        anchor={isRTL ? 'right' : 'left'}
        open={sidebarToggle}
        onClose={closeSidebar}
        variant="temporary"
        elevation={9}
      >
        <SidebarWrapper
          sx={{
            background:
              theme.palette.mode === 'dark'
                ? theme.colors.alpha.white[100]
                : darken(theme.colors.alpha.black[100], 0.5)
          }}
        >
          <Scrollbar>
            <Box mt={3}></Box>
            <Divider
              sx={{
                mt: theme.spacing(3),
                mx: theme.spacing(2),
                background: theme.colors.alpha.trueWhite[10]
              }}
            />
            <SidebarMenu />
          </Scrollbar>
        </SidebarWrapper>
      </Drawer>
    </>
  );
}

export default Sidebar;
